import * as Sentry from '@sentry/react';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isThisYear, isToday, isYesterday } from 'date-fns';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Badge } from '../../../../design-system/badge/badge';
import { Button } from '../../../../design-system/button/button';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { InvoiceEvidenceView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { isSupportedCurrency } from '../../../payments/utils/is-supported-currency';
import { getInvoiceDateCellContent } from './to-pay-invoice-table-columns-definition';
import { getJointAvatarDescription } from './utils/get-joint-avatar-description';
import { getPaidInvoiceMatchingStatus } from './utils/get-paid-invoice-matching-status';
import { getPaidInvoiceMatchingStatusBadgeVariant } from './utils/get-paid-invoice-matching-status-badge-variant';
import { sortPaidOnInvoiceDate } from './utils/sort-paid-on-invoice-date';

export const DATE_PAID_COLUMN_ID = 'paidDate';
export const INVOICE_INFO_COLUMN_ID = 'invoiceInfo';

const columnHelper = createColumnHelper<InvoiceEvidenceView>();

export const paidInvoiceTableColumnsDefinition = [
  columnHelper.accessor((row) => row.transaction?.fspDate ?? 'Unknown', {
    id: DATE_PAID_COLUMN_ID,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Date Paid" />,
    cell: ({ row }) => {
      const cellContent = getPaidDateCellContent(row.original.transaction?.fspDate);

      return <p className="text-sm font-normal leading-6 tracking-tight text-black">{cellContent}</p>;
    },
    sortingFn: sortPaidOnInvoiceDate,
    size: 12,
    minSize: 12,
  }),
  //
  // TODO: need to add the due date in the description of the avatar component
  // https://seapointfinance.slack.com/archives/D087F33BMUN/p1753800231636159
  //
  columnHelper.accessor(
    (row) => row.suggestedBeneficiary?.name ?? row.vendorInformation?.vendorCompanyName ?? 'Unknown',
    {
      id: INVOICE_INFO_COLUMN_ID,
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Vendor" />,
      cell: ({ row }) => {
        const cellContent =
          row.original.suggestedBeneficiary?.name ?? row.original.vendorInformation?.vendorCompanyName ?? 'Unknown';

        const description = getJointAvatarDescription({
          item1: getInvoiceDateCellContent(
            row.original.billingDates?.dueDate ?? row.original.billingDates?.invoiceDate,
          ),
          item2: row.original.invoiceDetails?.invoiceReference,
        });

        return <AvatarNameDescription avatar={{ showAvatar: true }} name={cellContent} description={description} />;
      },
      size: 25,
    },
  ),
  columnHelper.accessor((row) => row.transaction?.account, {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      const { name, logo } = {
        name: row.original.transaction?.account.institution?.name,
        logo: row.original.transaction?.account.institution?.logo,
      };

      const accountNameWithCurrency = `${name} (${row.original.transaction?.account.currency})`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
          name={accountNameWithCurrency ?? ''}
        />
      );
    },
    size: 20,
  }),
  columnHelper.accessor((row) => getPaidInvoiceMatchingStatus(row.isPaidUpdatedByType), {
    id: 'status',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Status" />,
    cell: ({ row }) => {
      const status = getPaidInvoiceMatchingStatus(row.original.isPaidUpdatedByType);
      const badgeVariant = getPaidInvoiceMatchingStatusBadgeVariant(status);

      return (
        <Badge variant={badgeVariant} shape="pill">
          {status}
        </Badge>
      );
    },
    size: 18,
    minSize: 18,
  }),
  columnHelper.accessor(
    (row) => {
      if (
        !row.invoiceDetails?.totalAmount ||
        !row.invoiceDetails?.currency ||
        !isSupportedCurrency(row.invoiceDetails?.currency)
      ) {
        return '—';
      }

      return getNormalizedAmount({
        amount: row.invoiceDetails.totalAmount,
        isCredit: true,
      });
    },
    {
      id: 'amount',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Amount" />,
      cell: ({ row }) => {
        if (
          !row.original.transaction?.amount ||
          !row.original.transaction?.currency ||
          !isSupportedCurrency(row.original.transaction?.currency)
        ) {
          return <p className="text-end font-normal leading-6 tracking-tight text-black">—</p>;
        }

        const amountValue = getAmountWithCurrency({
          amount: row.original.transaction?.amount,
          currency: row.original.transaction?.currency,
          isAccountBalance: true,
        });

        return <p className="text-end text-sm font-normal leading-6 tracking-tight text-black">{amountValue}</p>;
      },
      size: 15,
      minSize: 15,
    },
  ),
  columnHelper.display({
    id: 'reviewButton',
    cell: ({ row, table }) => {
      return (
        <Button
          variant="default"
          onClick={() => {
            if (!row.original?.transactionId) {
              Sentry.captureMessage('Transaction ID not found on paid invoice table columns definition', {
                extra: {
                  row,
                },
              });

              return;
            }

            table.options.meta?.onReviewInvoice?.(row.original.transactionId);
          }}
        >
          View
        </Button>
      );
    },
    size: 10,
    minSize: 10,
  }),
];

export const getPaidDateCellContent = (paidDate?: Date) => {
  if (!paidDate) {
    return 'Unknown';
  }

  if (isYesterday(paidDate)) {
    return 'Yesterday';
  }

  if (isToday(paidDate)) {
    return 'Today';
  }

  if (isThisYear(paidDate)) {
    return format(paidDate, 'dd MMM');
  }

  return format(paidDate, 'dd MMM yyyy');
};
